import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { setLeadSource } from '@/lib/leadSource';

/**
 * WhatsAppFloat — fixed bottom-right WhatsApp CTA.
 *
 * Replaces the previous orange "Assistente Prime Vision" chatbot bubble.
 * Real wa.me link, no widget, no modal.
 *
 * Positioning:
 *  - Desktop: bottom-5 right-5 — full label "Scrivici su WhatsApp"
 *  - Mobile:  bottom-20 right-3 — compact label "Scrivici", sits above the
 *             StickyMobileCTA bottom bar so they never overlap.
 *
 * Visibility:
 *  - Hidden while the cookie banner is on screen
 *  - Always visible afterwards
 *  - Respects (prefers-reduced-motion)
 */
const PHONE = '393520526856';

export const WhatsAppFloat = ({ leadSource = 'whatsapp-float' }) => {
  const { locale } = useLanguage();
  const [mounted, setMounted] = useState(false);
  // Hide while the cookie banner is on screen. Initial value from localStorage
  // to avoid a flash on first paint.
  const [cookieBannerOpen, setCookieBannerOpen] = useState(() => {
    try {
      return !localStorage.getItem('pv_cookie_consent');
    } catch {
      return false;
    }
  });

  useEffect(() => {
    setMounted(true);
    const onBannerVisible = (e) => setCookieBannerOpen(!!e.detail?.visible);
    window.addEventListener('pv:cookie-banner-visible', onBannerVisible);
    return () => window.removeEventListener('pv:cookie-banner-visible', onBannerVisible);
  }, []);

  if (!mounted || cookieBannerOpen) return null;

  const labelDesktop = locale === 'en' ? 'Message us on WhatsApp' : 'Scrivici su WhatsApp';
  const labelMobile = locale === 'en' ? 'Message us' : 'Scrivici';
  const msg = locale === 'en'
    ? 'Hi!%20I%27d%20like%20more%20information%20about%20Prime%20Vision.'
    : 'Ciao!%20Vorrei%20avere%20maggiori%20informazioni%20su%20Prime%20Vision.';

  return (
    <a
      href={`https://wa.me/${PHONE}?text=${msg}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="pv-whatsapp-float"
      aria-label={labelDesktop}
      onClick={() => setLeadSource(leadSource)}
      className="
        fixed z-[9999]
        bottom-20 right-3 md:bottom-5 md:right-5
        group inline-flex items-center gap-2.5
        pl-3 pr-4 py-3
        rounded-full text-white font-bold
        text-[13px] md:text-[13.5px]
        border border-white/20
        shadow-[0_14px_30px_-10px_rgba(31,183,85,0.55),0_2px_4px_rgba(31,183,85,0.15)]
        transition-transform duration-200
        hover:-translate-y-0.5 active:translate-y-0
      "
      style={{
        backgroundImage: 'linear-gradient(180deg, #2AC865 0%, #1FB755 100%)',
      }}
    >
      <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-white/20">
        <span className="absolute inset-0 rounded-full bg-white/30 animate-ping opacity-40" />
        <svg viewBox="0 0 24 24" className="relative w-4 h-4 fill-current" aria-hidden="true">
          <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.14 6.44 2.14 11.9c0 1.75.46 3.46 1.32 4.97L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.44 9.9-9.9a9.82 9.82 0 0 0-2.9-7.03ZM12.05 20.13h-.01a8.22 8.22 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 1 1 6.99 3.86Zm4.51-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23a7.49 7.49 0 0 1-1.38-1.72c-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.07-.1-.23-.16-.48-.29Z" />
        </svg>
      </span>
      <span className="hidden md:inline">{labelDesktop}</span>
      <span className="md:hidden">{labelMobile}</span>
    </a>
  );
};

export default WhatsAppFloat;
